// Library Methods
import { useCallback, useEffect, useMemo, useState } from "react";

// Components
import CustomAutocomplete from "src/components/shared/inputs/CustomAutocomplete/CustomAutocomplete";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useClinicSettingsContext } from "src/contexts/clinic/ClinicSettingsContext";

// Hooks
import { useClinicById } from "src/services/clinics/hooks/useClinicById";
import { useUpdateClinic } from "src/services/clinics/hooks/useUpdateClinic";

// Types
import { TimezoneType } from "src/types/sharedTypes";

// Utils
import { getAllTimeZoneInfo } from "src/utils/date/dateHelper";

// Constants
import { ONTARIO_TIMEZONES } from "src/constants/enums";

const TimezoneDropdown = () => {
  const { clinicInfo } = useClinicContext();
  const { currentSettings, handleUpdateSettings } = useClinicSettingsContext();

  const { mutate: editMutate, isPending: isEditPending } = useUpdateClinic();

  const {
    data: clinicData,
    isFetching: isFetchingClinic,
    isRefetching: isRefetchingClinic,
  } = useClinicById({ useToken: true, clinicId: clinicInfo?.id });

  const isUpdating = useMemo(
    () => isEditPending || isFetchingClinic || isRefetchingClinic,
    [isEditPending, isFetchingClinic, isRefetchingClinic]
  );

  // Info to display on the timezone dropdown
  const allDropdownOptions: Array<{
    displayName: string;
    value: string;
  }> = useMemo(
    () =>
      getAllTimeZoneInfo().map((tz: TimezoneType) => ({
        displayName: tz.name,
        value: tz.name.split(" (")?.[0],
      })),
    []
  );

  const [dropdownSelection, setDropdownSelection] = useState<{
    displayName: string;
    value: string;
  }>(allDropdownOptions?.[0]);

  // If the clinic is being edited, update the initial states with the existing clinic's info
  useEffect(() => {
    if (clinicData) {
      const selectedTZ =
        allDropdownOptions.find((tz) => tz?.value === clinicData.timezone) ??
        allDropdownOptions?.[0];

      setDropdownSelection({ ...selectedTZ });
    }
  }, [allDropdownOptions, clinicData]);

  // Handle editing an existing clinic
  const editClinic = useCallback(
    (timezone: string) => {
      if (!clinicData) {
        throw new Error();
      }

      editMutate({
        ...clinicData,
        timezone,
      });

      // Turn off the MTO option if the timezone is outside Ontario
      if (
        !ONTARIO_TIMEZONES.includes(timezone) &&
        currentSettings.MTOFixationMethod
      ) {
        handleUpdateSettings({
          ...currentSettings,
          MTOFixationMethod: false,
        });
      }
    },
    [clinicData, currentSettings, editMutate, handleUpdateSettings]
  );

  const handleDropdownChange = useCallback(
    (item: { displayName: string; value: string }) => {
      setDropdownSelection(item);

      if (allDropdownOptions?.find((option) => option.value === item.value)) {
        editClinic(item.value);
      }
    },
    [allDropdownOptions, editClinic]
  );

  return (
    <CustomAutocomplete
      id="timezone-autocomplete"
      data={allDropdownOptions}
      value={dropdownSelection}
      onChange={handleDropdownChange}
      isDisabled={isUpdating || isEditPending}
    />
  );
};

export default TimezoneDropdown;
